import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

export function HeroLeadForm({ idPrefix = "hero" }: { idPrefix?: string } = {}) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    website: "",
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim();
    const website = form.website.trim();

    if (!firstName) return toast.error("Please enter your first name.");
    if (!lastName) return toast.error("Please enter your last name.");
    if (!EMAIL_PATTERN.test(email)) return toast.error("Please enter a valid email.");
    if (!website) return toast.error("Please enter your website or social media link.");

    setSubmitting(true);
    try {
      const name = `${firstName} ${lastName}`;
      const response = await fetch("/api/public/contact-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          business: website,
          message: `Special offer call request from ${window.location.pathname}\n\nName: ${name}\nEmail: ${email}\nWebsite or social media link: ${website}`,
          formType: "contact",
          pagePath: window.location.pathname,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || "Failed to send");
      window.location.assign("/thank-you-next-steps");
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again or email hello@threedaylaunch.com.");
      setSubmitting(false);
    }
  };

  return (
    <form action="/api/public/contact-notify" method="post" onSubmit={handleSubmit} className="mt-6 space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-first-name`}>Name</Label>
          <Input
            id={`${idPrefix}-first-name`}
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            required
            maxLength={50}
            autoComplete="given-name"
            placeholder="First name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-last-name`}>Last Name</Label>
          <Input
            id={`${idPrefix}-last-name`}
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            required
            maxLength={50}
            autoComplete="family-name"
            placeholder="Last name"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-email`}>Email</Label>
        <Input
          id={`${idPrefix}-email`}
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          required
          maxLength={255}
          autoComplete="email"
          placeholder="you@example.com"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-website`}>Website (or social media link)</Label>
        <Input
          id={`${idPrefix}-website`}
          name="website"
          type="text"
          value={form.website}
          onChange={handleChange}
          required
          maxLength={150}
          autoComplete="url"
          placeholder="https://yourwebsite.com"
        />
      </div>
      <Button
        type="submit"
        size="lg"
        disabled={submitting}
        className="w-full whitespace-normal bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold h-auto min-h-14 py-3 rounded-full shadow-glow text-base"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
          </>
        ) : (
          <>
            Book a Free Call to Claim Now <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
    </form>
  );
}
